import { Parser } from 'expr-eval';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { validationRules } from '../datatable/custom-datatable/validation';
import { AlertService } from 'src/app/services/alert.service';
import { UploadHandlerService } from 'src/app/shared/uploadFileHandler.service';
import { EventService } from 'src/app/services/event.service';
import { INDEX_MULTIPLIER } from 'src/app/shared/constants';
import { DropColumn } from 'src/app/shared/commonFunction';

export interface ColumnSchema {
  field: string;
  name: string;
  type: 'number' | 'string' | 'select';
  formula?: string; // cell-level
  options?: string[];
  summaryType?: 'avg' | 'sum' | 'min' | 'max'; // summary row
  summaryFormula?: string; // optional override
}
export interface ComparisonRow {
  id: string;
  values: { [key: string]: any };
  formulas?: { [key: string]: string }; // row-level formula overrides
}

@Component({
  selector: 'app-custom-comps-grid',
  templateUrl: './custom-comps-grid.component.html',
  styleUrls: ['./custom-comps-grid.component.scss']
})
export class CustomCompsGridComponent implements OnInit {
  @Input() showImportButton: boolean = false;
  @Input() showAdjustColumns: boolean = false;
  @Input() schemaName: string = '';
  @Input() isVerticalTable: boolean = false;
  @Input() isAdjustive: boolean = false;
  @Input() columns: any[] = [];
  @Input() defaultData: any = {};
  @Input() verticleTableHeaderTitle: string = '';
  @Input() onCellRender: Function;
  @Input() data: any[] = [];
  @Input() showImportFileButton: boolean = false;
  @Input() subjectPropertyId: any;
  @Input() locationCoordinates: any;

  @Output() dataChange = new EventEmitter<any>();
  @Input() showMap: boolean = false;
  editingColumnIndex: number | null = null;
  newRow: any = {};
  errors: any = {};
  isEditingAll = false; // Tracks whether all fields are editable
  isSettingsOpen: boolean = false;
  columnsCopy: any[] = [];
  tableVerticleView: boolean = false;
  showNewInputRow: boolean = false
  editingRow: any;
  rowToDelete: any;
  isImportModalOpen = false;
  isDeleteModalOpen: boolean = false;
  objectKeys = Object.keys;
  displayedColumns: any = [];
  @Input() properties: any = [];
  @Output() filterApplied = new EventEmitter<any>();

  @Output() addComps = new EventEmitter<any>();
  selectedComps: any = [];
  newRows: any[] = [{}]; // Start with one empty row

  @Input() totalCount: number = 0;
  @Input() pageSize: number = 10;
  @Input() offset: number = 0;
  @Input() currentPage: number = 1;
  @Output() pageChange = new EventEmitter<number>();

  enableSummary: boolean = false;
  enableAdjustment: boolean = false;
  groupedVisibleColumns: any = []
  @Input() propertyDetail: any;
  stringify = JSON.stringify;
  @Output() loadMore = new EventEmitter<any>();
  cloneData: any = [];
  indexMultiplier:number=INDEX_MULTIPLIER;

  constructor(
    private alertService: AlertService,
    private uploadHandler: UploadHandlerService,
    private broadcaster: EventService
  ) { }
  ngOnInit() {
    this.tableVerticleView = this.isVerticalTable;
    // this.columnsCopy = JSON.parse(JSON.stringify(this.columns));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.columns && this.columns && this.columns.length) {
      this.updateColumnIndexes(this.columns)
      this.groupVisibleColumns();
    }
    if (changes['data'] || changes['schema']) {
      this.evaluateAllRows();
    }
    if (changes['data'] && changes['data'].currentValue?.length) {
  
      this.locationCoordinates = this.data.reduce((acc, p) => {
        if (p.lat && p.lng) {
          acc.push({
            lat: Number(p.lat),
            lng: Number(p.lng)
          });
        }
        return acc;
      }, []);
  
      if (this.propertyDetail?.latitude && this.propertyDetail?.longitude) {
        this.locationCoordinates.push({
          lat: Number(this.propertyDetail.latitude),
          lng: Number(this.propertyDetail.longitude)
        });
      }
    }
  }

  groupVisibleColumns() {
    const noHeaderTitleKey = 'General';

    const result = {};

    this.columns.forEach(col => {
      const title = col.header_title || noHeaderTitleKey;

      if (!result[title]) {
        result[title] = [];
      }

      result[title].push(col);

    });
    if (result[noHeaderTitleKey]) {
      this.groupedVisibleColumns = {
        [noHeaderTitleKey]: result[noHeaderTitleKey],
        ...Object.keys(result)
          .filter(k => k !== noHeaderTitleKey)
          .reduce((acc, k) => {
            acc[k] = result[k];
            return acc;
          }, {})
      };
    } else {
      this.groupedVisibleColumns = result;
    }

  }


  

  updateColumnIndexes(columns: any[]): void {
    columns.forEach((col, i) => {
      if (!col.hasOwnProperty("index") || typeof col.index !== 'number' || col.index < 10000) {
        col.index = (i + 1) * this.indexMultiplier;
        if (col.index < 10000) {
          col.index += 10000;
        }
      }
    });
  
    this.columns = columns;
  }

  formatCurrency(value: number, locale: string = 'en-US', currency: string = 'USD'): string {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value && !isNaN(value) ? value : 0);
  }


  formatNumber(value: number, locale: string = 'en-US'): string {
    return new Intl.NumberFormat(locale).format(value);
  }


  renderCell(data) {
    return this.onCellRender(data);
  }

  startEdit(index: number) {
    this.editingRow = index;
  }

  // Start Editing a Column (All Rows in that Column)
  startEditCol(colIndex: number) {
    this.editingColumnIndex = colIndex;
  }


  startEditAll() {
    this.isEditingAll = true;
  }


  validateField(field: string, type: string, index?: any, data?: any, isNewRow?: boolean) {
    const column = this.columns.find(col => col.field === field);
    const value = data[field]
    this.errors = this.errors || { newRows: [] };

    let errorMessage = null;

    if (column && column.validations) {
      errorMessage = this.applyValidations(value, column.name, column.validations);
    }

    if (!errorMessage && column && column.type === 'number' && value < 0) {
      errorMessage = `${column.name} must be a positive number.`;
    }

    if (index !== undefined) {
      if (isNewRow) {
        this.errors.newRows = this.errors.newRows || [];
        this.errors.newRows[index] = {
          ...(this.errors.newRows[index] || {}),
          [field]: errorMessage
        };
      } else {
        this.errors[index] = {
          ...(this.errors[index] || {}),
          [field]: errorMessage
        };
      }
    } else {
      this.errors[field] = errorMessage;
    }

    if (isNewRow && index === this.newRows.length - 1) {
      const currentRowErrors = this.errors?.newRows?.[index];
      const isRowValid = !currentRowErrors || Object.values(currentRowErrors).every(err => !err);

      const currentRow = this.newRows[index];
      const hasAnyValue = Object.values(currentRow ?? {}).some(
        val => val !== null && val !== undefined && val !== ''
      );

      if (isRowValid && hasAnyValue) {
        this.newRows.push({});
      }
    }
  }


  applyValidations(value: any, fieldTitle: string, validations: string[]) {
    for (const rule of validations) {
      const message = validationRules[rule](value, fieldTitle);
      if (message !== true) {
        return message;
      }
    }
    return null;

  };


  saveAllEdits() {
    const rowsToValidate = this.newRows.slice(0, -1);
    const updatedRows = [...this.data];
    this.errors = { newRows: [] };
    let isValid = true;

    const filteredColumns = this.columns.filter(col => col.visible === true || col.visible === 'true');

    if (this.data && this.data.length) {
      this.data.forEach((row, rowIndex) => {
        this.errors[rowIndex] = {};
        filteredColumns.forEach(col => {
          if (col.type !== 'custom') {
            this.validateField(col.field, col.type, rowIndex, row);
            if (this.errors[rowIndex][col.field]) {
              isValid = false;
            }
          }
        });
      });
    }

    const validNewRows = [];
    if (this.newRows && this.newRows.length) {

      rowsToValidate.forEach((newRow, newIndex) => {
        const hasValues = Object.values(newRow ?? {}).some(
          value => value !== null && value !== undefined && value !== ''
        );

        if (!hasValues) return;

        this.errors.newRows[newIndex] = {};
        filteredColumns.forEach(col => {
          this.validateField(col.field, col.type, newIndex, newRow, true);
          if (this.errors.newRows[newIndex][col.field]) {
            isValid = false;
          }
        });

        validNewRows.push(newRow);
      });
    }

    if (!isValid) {
      return;
    }

    const allData = [...updatedRows, ...validNewRows];
  
      this.dataChange.emit({
        data: allData.length ? allData : [],
        type: this.data?.length ? 'edit' : 'add',
        schemaName: this.schemaName
      });
  
      // Reset new rows with a single empty row again
      this.newRows = [{}];
      this.isEditingAll = false;
      if(!this.areObjectArraysEqual(this.columnsCopy,this.columns)){
        this.dataChange.emit({
          data: {columns:this.columns,view:this.isVerticalTable?'V_TABLE':'H_TABLE'},
          type: 'columns-order',
          schemaName: this.schemaName
        });
      }
  }
  
  areObjectArraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;
  
    const sorted1 = [...arr1].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    const sorted2 = [...arr2].sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  
    return sorted1.every((obj, index) => this.deepEqual(obj, sorted2[index]));
  }
  
  deepEqual(a: any, b: any): boolean {
    if (a === b) return true;
  
    if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
      return false;
    }
  
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
  
    for (const key of keysA) {
      if (!keysB.includes(key) || !this.deepEqual(a[key], b[key])) return false;
    }
  
    return true;
  }


  cancelEditAll() {
    this.isEditingAll = false;
    this.columnsCopy = JSON.parse(JSON.stringify(this.columns));
    this.errors = {};
    this.data = this.cloneData;
  }

 
  deleteRow(index: number) {
    this.dataChange.emit({ data: index, type: 'delete', schemaName: this.schemaName });
  }



  openDeleteModal(row: any, isNewRow: boolean = false) {
    this.rowToDelete = row;
    if (isNewRow) {
      const index = row;
      this.newRows.splice(index, 1)
    }
    else {
      this.data.splice(this.rowToDelete, 1)
    }
  }

  confirmDelete() {
    if (this.rowToDelete !== undefined) {
      this.data.splice(this.rowToDelete, 1)
    }
    this.isDeleteModalOpen = false;
    this.rowToDelete = null;
  }


  openSettings() {
    this.isSettingsOpen = !this.isSettingsOpen;
    if (this.columns && Array.isArray(this.columns)) {
      this.columnsCopy = JSON.parse(JSON.stringify(this.columns));
    }
    this.tableVerticleView = this.isVerticalTable;
  }

  applySettings(event) {
    this.dataChange.emit(event);
    this.isSettingsOpen = false;
  }



  openImportModal() {
    this.isImportModalOpen = true;
  }

  closeImportModal() {
    this.isImportModalOpen = false;
  }



  onColumnDrop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex);
    }

  }

  getSelectedComps(comps) {
    this.selectedComps = comps
  }

  addComparable() {
    this.addComps.emit(this.selectedComps);
    this.closeImportModal()
  }

  onInputChange(rowIndex: number, field: string, type: string) {
    this.validateField(field, type, rowIndex, this.newRows[rowIndex], true);

    // If we're editing the last row, auto-add a new empty row
    if (rowIndex === this.newRows.length - 1) {
      const isNotEmpty = Object.values(this.newRows[rowIndex]).some(val => val !== null && val !== '');
      if (isNotEmpty) {
        this.newRows.push({});
      }
    }
  }


  onPageChanged(newPage: number) {
    this.currentPage = newPage;
    this.pageChange.emit(newPage);
  }

  getDisplayValue(row: any, col: any, adjustments: boolean = false): string {
    if (!row || !col || !col.field) return 'NA';
    const value = adjustments && row['adjustments']?.[col.field] !== undefined ? row['adjustments'][col.field] : row[col.field];

    switch (col.unit) {
      case 'percentage':
        return (value != null ? (+value).toFixed(2) + '%' : '0%');

      case 'currency':
        return this.formatCurrency?.(value) || '$0';

      case 'time':
        return (value != null ? (+value).toFixed(2) + 'ms' : '0ms');

      case 'number':
        return this.formatNumber?.(value) || '0';

      case 'date':
        return value ? new Date(value).toLocaleDateString('en-US') : 'NA';

      case 'distance':
        return value != null ? (+value).toFixed(2) + ' ' + (row[col.unit] || '') : '0';

      case 'custom':
        return '';

      default:
        return value != null ? value : 'NA';
    }
  }

  async handleFileUpload(event: Event) {
    this.broadcaster.BroadcastEvent("LOADER_EVENT", "show")
    this.uploadHandler.handleFileUpload(
      event,
      this.propertyDetail && this.propertyDetail.id ? this.propertyDetail?.id : this.subjectPropertyId,
      this.schemaName,
      async () => {
        this.dataChange.emit({ data: this.data, type: 'import', schemaName: this.schemaName });
        this.broadcaster.BroadcastEvent("LOADER_EVENT", "hide")

      },
      (err) => {
        console.error(err);
        this.broadcaster.BroadcastEvent("LOADER_EVENT", "hide")

      }
    );
  }

  dragStartIndex: number | null = null;
  dragStartCol: any = null;

  onDragStart(event: DragEvent, col: any): void {
    this.dragStartCol = col;
    this.dragStartIndex = col.index;
    event.dataTransfer?.setData('text/plain', this.dragStartIndex.toString());
    event.dataTransfer?.setDragImage(event.target as Element, 0, 0);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  

  onDrop(event: DragEvent, dropCol: any): void {
    let newIndex = DropColumn(this.columns,this.dragStartCol,dropCol)
    this.dragStartCol.index = newIndex;
    this.dragStartCol = null;
    this.groupVisibleColumns()
    
  }

  areObjectsIdentical(obj1: any, obj2: any): boolean {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
  }


  onDragEnter(event: DragEvent): void {
    (event.target as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    (event.target as HTMLElement).classList.remove('drag-over');
  }
onFieldBlur(field: string, type: string, index: number, row: any) {
  this.validateField(field, type, index, row, true);
}


  fetchMore() {
    this.loadMore.emit()
  }


  selectedCell = {
    rowIndex: 0,
    colKey: '',
    formula: '',
    label: ''
  };
  @ViewChild('formulaBar') formulaBar!: ElementRef<HTMLInputElement>;
  selectCell(rowIndex: number, col: ColumnSchema) {
    let row = this.data[rowIndex];
    if (!row) {
      const newRowIndex = rowIndex - this.data.length;
      row = this.newRows[newRowIndex];
    }
    const formula = row.formulas?.[col.field] ?? col.formula ?? '';
    this.selectedCell = {
      rowIndex,
      colKey: col.field,
      label: col.name,
      formula
    };
    this.formulaBar.nativeElement.focus();
  }

  applyFormulaFromBar() {
    if (!this.selectedCell) return;

    const { rowIndex, colKey, formula } = this.selectedCell;
    let row = null;
    if (this.data[rowIndex]) {
      this.data[rowIndex] = JSON.parse(JSON.stringify(this.cloneData[rowIndex]));
      row = this.data[rowIndex];
    }
    else {
      row = this.newRows[rowIndex - this.data.length];
    }

    if (!row.formulas || Object.keys(row.formulas).length == 0) {
      row.formulas = {};
    }
    row.formulas[colKey] = formula;

    const updateData = this.evaluateRow(row);
    if (this.data[rowIndex]) {
      this.cloneData[rowIndex] = updateData;
    }
  }

  clearSelectedCell() {
    this.selectedCell = {
      rowIndex: 0,
      colKey: '',
      formula: '',
      label: ''
    };
  }

  evaluateRow(row: any): any {
    const parser = new Parser();
    const context = { ...row };


    for (const col of this.columns) {
      const formula =
        row.formulas?.[col.field] ?? col.formula;

      if (formula) {
        try {
          const expr = parser.parse(formula);
          const result = expr.evaluate(context);
          if (!row?.adjustments) {
            Object.assign(row, { 'adjustments': {} });
          }
          row['adjustments'][col.field] = Number.isFinite(result) ? result : '';
        } catch (err) {
          console.warn(`Error evaluating formula for ${col.key}:`, err);
          // row[col.field] = '';
        }
      }
    }

    return row;
  }

  evaluateAllRows(): void {
    if (!this.cloneData.length) {
      this.cloneData = JSON.parse(JSON.stringify(this.data));
    }
    this.data = this.data.map(row => this.evaluateRow(row));
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then((res) => {
      this.alertService.success("Copied")
    })
  }

}
